import clamp from 'lodash/clamp.js';
import { getRelativeStrokeWidth, getRelativeThickness, isInProgress, isComplete, isNotStarted, queue, getTrickleProgress } from './utils.js';

var script = {
  name: 'CircleProgress',
  props: {
    value: {
      type: Number,
      default: 0,
      required: true,
      validator: val => val >= 0 && val <= 100
    },
    thickness: {
      type: Number,
      default: 0
    },
    strokeWidth: {
      type: Number,
      default: 1
    },
    width: {
      type: Number,
      default: 200
    },
    colors: {
      type: Object,
      default: () => ({
        center: {
          fill: '#ffffff',
          stroke: '#ffffff'
        },
        default: {
          fill: '#DADCE5',
          stroke: '#dadce5'
        },
        progress: {
          fill: '#DB1111',
          stroke: '#6A0000'
        },
        complete: {
          fill: '#00C583',
          stroke: '#005027'
        }
      })
    },
    trickle: {
      type: Boolean,
      default: false
    },
    trickleSpeed: {
      type: Number,
      default: 200
    },
    speed: {
      type: Number,
      default: 200
    }
  },
  data() {
    return {
      cx: 50,
      cy: 50,
      internalValue: this.value,
      minInProgressValue: 2,
      maxInProgressValue: 99.4
    };
  },
  computed: {
    relativeStrokeWidth() {
      const {
        strokeWidth,
        width
      } = this;
      return getRelativeStrokeWidth({
        strokeWidth,
        width
      });
    },
    relativeThickness() {
      const {
        thickness,
        width
      } = this;
      return getRelativeThickness({
        thickness,
        width
      });
    },
    maxFillSize() {
      return this.width - this.fillRadius;
    },
    isInProgress() {
      return isInProgress(this.internalValue);
    },
    isComplete() {
      return isComplete(this.internalValue);
    },
    isNotStarted() {
      return isNotStarted(this.internalValue);
    },
    fillRadius() {
      return this.innerRadius - this.relativeStrokeWidth;
    },
    innerRadius() {
      return this.cx - this.relativeThickness;
    },
    outerRadius() {
      return this.cx - this.relativeStrokeWidth;
    },
    relativeCoords() {
      const value = this.internalValue / 100;
      const exprBase = -value * 2 * Math.PI + 0.5 * Math.PI;
      return {
        x: Math.cos(exprBase),
        y: -Math.sin(exprBase)
      };
    },
    outerProgressArc() {
      const path = [];
      path.push(`M 50 ${this.relativeStrokeWidth}`);
      path.push(`A ${this.outerRadius} ${this.outerRadius}`);
      path.push('0');
      path.push(this.internalValue > 50 ? '1' : '0');
      path.push('1');
      path.push(this.getArcCoords(this.outerRadius));
      path.push('L 50 50');
      path.push('z');
      return path.join(' ');
    },
    innerProgressArc() {
      const path = [];
      path.push(`M 50 ${this.relativeThickness}`);
      path.push(`A ${this.innerRadius} ${this.innerRadius}`);
      path.push('0');
      path.push(this.internalValue > 50 ? '1' : '0');
      path.push('1');
      path.push(this.getArcCoords(this.innerRadius));
      path.push('L 50 50');
      path.push('z');
      return path.join(' ');
    }
  },
  watch: {
    value() {
      if (!this.trickle) {
        this.internalValue = this.value;
        return;
      }
      const value = clamp(this.value, this.internalValue, 100);
      if (value === 100) {
        this.done();
      } else {
        this.increment(value);
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    getArcCoords(radius) {
      const coordX = Math.round((this.relativeCoords.x * radius + 50) * 100) / 100;
      const coordY = Math.round((this.relativeCoords.y * radius + 50) * 100) / 100;
      return `${coordX} ${coordY}`;
    },
    increment(amount) {
      if (this.isNotStarted) {
        this.start();
      }
      if (this.isComplete) {
        return;
      }
      if (typeof amount !== 'number') {
        amount = this.internalValue + getTrickleProgress(this.internalValue);
      }
      amount = clamp(amount, 0, this.maxInProgressValue);
      this.set(amount);
    },
    done(force) {
      if (!force && !this.internalValue) {
        return;
      }
      this.increment(30 + 50 * Math.random());
      this.set(100);
    },
    start() {
      if (this.isNotStarted) {
        this.set(0);
      }
      if (this.trickle) {
        this.timer = setInterval(() => {
          this.increment();
          if (this.isComplete) {
            clearInterval(this.timer);
          }
        }, this.trickleSpeed);
      }
    },
    set(value) {
      value = clamp(value, this.minInProgressValue, 100);
      queue(next => {
        if (value === 1) {
          setTimeout(() => {
            setTimeout(() => {
              this.internalValue = 100;
              next();
            }, this.speed);
          }, this.speed);
        } else {
          this.internalValue = value;
          setTimeout(next, this.speed);
        }
      });
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-progress-circle.vue2.js.map
